import { ROAD_CONNECT_E, ROAD_CONNECT_N, ROAD_CONNECT_S, ROAD_CONNECT_W } from "./roadConnectFlags";

function connectionCount(mask: number): number {
  let count = 0;
  if (mask & ROAD_CONNECT_N) count += 1;
  if (mask & ROAD_CONNECT_E) count += 1;
  if (mask & ROAD_CONNECT_S) count += 1;
  if (mask & ROAD_CONNECT_W) count += 1;
  return count;
}

function straightMaskThroughOppositeBuilding({
  roadMask,
  buildingMask,
}: {
  roadMask: number;
  buildingMask: number;
}): number | undefined {
  if (roadMask & ROAD_CONNECT_N && buildingMask & ROAD_CONNECT_S) {
    return ROAD_CONNECT_N | ROAD_CONNECT_S;
  }
  if (roadMask & ROAD_CONNECT_S && buildingMask & ROAD_CONNECT_N) {
    return ROAD_CONNECT_N | ROAD_CONNECT_S;
  }
  if (roadMask & ROAD_CONNECT_E && buildingMask & ROAD_CONNECT_W) {
    return ROAD_CONNECT_E | ROAD_CONNECT_W;
  }
  if (roadMask & ROAD_CONNECT_W && buildingMask & ROAD_CONNECT_E) {
    return ROAD_CONNECT_E | ROAD_CONNECT_W;
  }
  return undefined;
}

function buildingFlankedStraightMask({
  buildingMask,
  hasNorthSouthRoad,
  hasEastWestRoad,
}: {
  buildingMask: number;
  hasNorthSouthRoad: boolean;
  hasEastWestRoad: boolean;
}): number | undefined {
  const buildingEast = buildingMask & ROAD_CONNECT_E;
  const buildingWest = buildingMask & ROAD_CONNECT_W;
  const buildingNorth = buildingMask & ROAD_CONNECT_N;
  const buildingSouth = buildingMask & ROAD_CONNECT_S;

  if (buildingEast && buildingWest) {
    return hasNorthSouthRoad ? ROAD_CONNECT_N | ROAD_CONNECT_S : ROAD_CONNECT_E | ROAD_CONNECT_W;
  }

  if (buildingNorth && buildingSouth) {
    return hasEastWestRoad ? ROAD_CONNECT_E | ROAD_CONNECT_W : ROAD_CONNECT_N | ROAD_CONNECT_S;
  }

  return undefined;
}

function eastWestBuildingCorridorMask({
  roadMask,
  roadCount,
  buildingMask,
  hasNorthSouthRoad,
}: {
  roadMask: number;
  roadCount: number;
  buildingMask: number;
  hasNorthSouthRoad: boolean;
}): number | undefined {
  if (hasNorthSouthRoad) return undefined;

  const buildingEast = buildingMask & ROAD_CONNECT_E;
  const buildingWest = buildingMask & ROAD_CONNECT_W;
  if (buildingEast && buildingWest) return ROAD_CONNECT_E | ROAD_CONNECT_W;

  if (roadCount !== 1 || !(roadMask & (ROAD_CONNECT_E | ROAD_CONNECT_W))) return undefined;

  if (buildingEast && roadMask & ROAD_CONNECT_W) return ROAD_CONNECT_E | ROAD_CONNECT_W;
  if (buildingWest && roadMask & ROAD_CONNECT_E) return ROAD_CONNECT_E | ROAD_CONNECT_W;

  return undefined;
}

function northSouthRoadWithSideBuildingCorner({
  roadMask,
  roadCount,
  buildingMask,
}: {
  roadMask: number;
  roadCount: number;
  buildingMask: number;
}): number | undefined {
  if (roadCount !== 1 || !(roadMask & (ROAD_CONNECT_N | ROAD_CONNECT_S))) return undefined;

  const buildingWest = buildingMask & ROAD_CONNECT_W;
  const buildingEast = buildingMask & ROAD_CONNECT_E;
  if (buildingWest && buildingEast) return undefined;

  const roadNorth = roadMask & ROAD_CONNECT_N;
  const roadSouth = roadMask & ROAD_CONNECT_S;

  if (buildingWest && roadSouth) return ROAD_CONNECT_W | ROAD_CONNECT_S;
  if (buildingWest && roadNorth) return ROAD_CONNECT_W | ROAD_CONNECT_N;
  if (buildingEast && roadSouth) return ROAD_CONNECT_E | ROAD_CONNECT_S;
  if (buildingEast && roadNorth) return ROAD_CONNECT_E | ROAD_CONNECT_N;

  return undefined;
}

function northSouthBuildingCorridorMask({
  buildingMask,
  hasEastWestRoad,
}: {
  buildingMask: number;
  hasEastWestRoad: boolean;
}): number | undefined {
  if (hasEastWestRoad) return undefined;
  if (buildingMask & ROAD_CONNECT_N && buildingMask & ROAD_CONNECT_S) {
    return ROAD_CONNECT_N | ROAD_CONNECT_S;
  }
  return undefined;
}

function singleConnectionRoadMaskWithBuildings({
  roadMask,
  roadCount,
  buildingMask,
  hasNorthSouthRoad,
  hasEastWestRoad,
}: {
  roadMask: number;
  roadCount: number;
  buildingMask: number;
  hasNorthSouthRoad: boolean;
  hasEastWestRoad: boolean;
}): number | undefined {
  if (roadCount !== 1) return undefined;

  if (hasNorthSouthRoad || hasEastWestRoad) {
    const straight = straightMaskThroughOppositeBuilding({ roadMask, buildingMask });
    if (straight !== undefined) return straight;
  }

  if (hasNorthSouthRoad) {
    const sideBuildingCorner = northSouthRoadWithSideBuildingCorner({
      roadMask,
      roadCount,
      buildingMask,
    });
    if (sideBuildingCorner !== undefined) return sideBuildingCorner;
  }

  if (hasNorthSouthRoad || hasEastWestRoad) {
    if (buildingMask !== 0) {
      const combined = roadMask | buildingMask;
      if (connectionCount(combined) <= 2) return combined;
    }

    return roadMask;
  }

  if (buildingMask !== 0) {
    const combined = roadMask | buildingMask;
    if (connectionCount(combined) <= 2) return combined;
    return roadMask;
  }

  return undefined;
}

export function resolveRoadAutotileMaskWithBuildings({
  roadMask,
  buildingMask,
}: {
  roadMask: number;
  buildingMask: number;
}): number | undefined {
  const roadCount = connectionCount(roadMask);
  const hasNorthSouthRoad = !!(roadMask & (ROAD_CONNECT_N | ROAD_CONNECT_S));
  const hasEastWestRoad = !!(roadMask & (ROAD_CONNECT_E | ROAD_CONNECT_W));

  const flankedStraight = buildingFlankedStraightMask({
    buildingMask,
    hasNorthSouthRoad,
    hasEastWestRoad,
  });
  if (flankedStraight !== undefined) return flankedStraight;

  const eastWestCorridor = eastWestBuildingCorridorMask({
    roadMask,
    roadCount,
    buildingMask,
    hasNorthSouthRoad,
  });
  if (eastWestCorridor !== undefined) return eastWestCorridor;

  const northSouthCorridor = northSouthBuildingCorridorMask({ buildingMask, hasEastWestRoad });
  if (northSouthCorridor !== undefined) return northSouthCorridor;

  return singleConnectionRoadMaskWithBuildings({
    roadMask,
    roadCount,
    buildingMask,
    hasNorthSouthRoad,
    hasEastWestRoad,
  });
}
